import { Columns3 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';

import { ElegirLicencias } from './ElegirLicencias';
import { FiltrosBiblioteca } from './FiltrosBiblioteca';
import { useAsignar, useBiblioteca, useResumenBiblioteca } from './queries';

const CLAVES = ['q', 'type', 'specialtyId', 'english', 'm'] as const;
const MAX_COLUMNAS = 12;

function Matriz() {
  const { filtros, cambiar, query } = useFiltrosUrl(CLAVES);
  const editable = useCan('contenido', 'editar');
  const resumen = useResumenBiblioteca();
  const asignar = useAsignar();
  const [eligiendo, setEligiendo] = useState(false);

  const columnas = filtros.m
    .split(',')
    .map(Number)
    .filter((n) => n > 0);
  const licencias = (resumen.data?.memberships ?? []).filter((m) => columnas.includes(m.id));
  const filtrosApi = { ...query, m: undefined };
  const q = useBiblioteca({ ...filtrosApi, page: filtros.page, pageSize: 50 });
  const pagina = q.data?.data ?? [];

  const cambiarCelda = (itemIds: number[], membershipId: number, poner: boolean) =>
    asignar.mutate(
      { action: poner ? 'add' : 'remove', libraryItemIds: itemIds, membershipIds: [membershipId] },
      {
        onSuccess: (r) => {
          if (itemIds.length > 1) toast.success(`${r.changed} cambio(s).`);
        },
      },
    );

  return (
    <>
      <Encabezado
        titulo="Contenido × licencia"
        descripcion="Marca qué licencias incluyen cada elemento. Cada casilla se guarda al momento."
        acciones={
          <>
            <Link to="/contenido" className="btn-cs btn-contorno">
              Volver a la biblioteca
            </Link>
            <button
              type="button"
              className="btn-cs btn-primario"
              onClick={() => setEligiendo(true)}
            >
              <Columns3 size={16} aria-hidden /> Elegir licencias ({columnas.length})
            </button>
          </>
        }
      />
      <FiltrosBiblioteca filtros={filtros} cambiar={cambiar} claves={CLAVES} />

      {columnas.length === 0 && (
        <Vacio titulo="Elige las licencias que quieres comparar">
          <p className="text-sm text-texto-3">Hasta {MAX_COLUMNAS} columnas a la vez.</p>
          <button
            type="button"
            className="btn-cs btn-primario mt-4"
            onClick={() => setEligiendo(true)}
          >
            Elegir licencias
          </button>
        </Vacio>
      )}
      {columnas.length > 0 && q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {columnas.length > 0 && q.data?.data.length === 0 && (
        <Vacio titulo="Sin contenido con esos filtros" />
      )}
      {columnas.length > 0 && pagina.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Elemento</th>
                {licencias.map((m) => {
                  const todos = pagina.every((e) => e.membershipIds.includes(m.id));
                  return (
                    <th key={m.id} className="min-w-28 text-center align-bottom">
                      <span className="block text-xs leading-tight">{m.name}</span>
                      <span className="block text-[11px] font-normal text-texto-3">
                        {m.itemsCount} en total
                      </span>
                      {editable && (
                        <button
                          type="button"
                          className="mt-1 text-[11px] font-semibold text-azul-texto underline disabled:opacity-50"
                          disabled={asignar.isPending}
                          onClick={() =>
                            cambiarCelda(
                              pagina.map((e) => e.id),
                              m.id,
                              !todos,
                            )
                          }
                        >
                          {todos ? 'Quitar página' : 'Marcar página'}
                        </button>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pagina.map((e) => (
                <tr key={e.id}>
                  <td className="max-w-sm">
                    <span className="font-semibold">{e.name}</span>
                    <span className="block text-xs text-texto-3">
                      #{e.id} · {e.typeLabel}
                      {e.specialtyName && ` · ${e.specialtyName}`}
                    </span>
                  </td>
                  {licencias.map((m) => (
                    <td key={m.id} className="text-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[var(--color-azul-600)]"
                        aria-label={`${e.name} en ${m.name}`}
                        checked={e.membershipIds.includes(m.id)}
                        disabled={!editable || asignar.isPending}
                        onChange={(ev) => cambiarCelda([e.id], m.id, ev.target.checked)}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {columnas.length > 0 && q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}

      <Modal
        abierto={eligiendo}
        onCerrar={() => setEligiendo(false)}
        titulo="Licencias en columnas"
        descripcion={`Elige hasta ${MAX_COLUMNAS}.`}
        ancho="max-w-2xl"
      >
        {resumen.data && (
          <ElegirLicencias
            licencias={resumen.data.memberships}
            elegidas={new Set(columnas)}
            onCambio={(id, marcada) => {
              if (marcada && columnas.length >= MAX_COLUMNAS) {
                toast.error(`Máximo ${MAX_COLUMNAS} licencias a la vez.`);
                return;
              }
              const next = marcada ? [...columnas, id] : columnas.filter((c) => c !== id);
              cambiar({ m: next.join(','), page: filtros.page });
            }}
          />
        )}
        <div className="mt-5 flex justify-end">
          <button type="button" className="btn-cs btn-primario" onClick={() => setEligiendo(false)}>
            Listo
          </button>
        </div>
      </Modal>
    </>
  );
}

export function MatrizPage() {
  return (
    <PermissionGate seccion="contenido">
      <Matriz />
    </PermissionGate>
  );
}
